import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { FixedAssetReportService } from '../../services/fixedAssetReport.service';
import { TieOutReport } from '../../interfaces/fixedAsset/fixedAssetReport.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';
import { toLocalIsoDate } from '../../utils/dateUtils';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => toLocalIsoDate();

const FixedAssetTieOutReport: React.FC = () => {
  const toast = useToast();
  const [asOf, setAsOf] = useState(today());
  const [report, setReport] = useState<TieOutReport | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const data = await FixedAssetReportService.tieOut(institutionId!, asOf);
      setReport(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load tie-out report');
    } finally {
      setLoading(false);
    }
  };

  // Open with the figures showing, using today's date.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['Measure', 'Register', 'GL', 'Variance'];
    const dataRows = [
      ['Asset cost', report.registerCost, report.glAssetBalance, report.costVariance],
      ['Accumulated depreciation', report.registerAccumulated, report.glAccumulatedBalance, report.accumulatedVariance],
    ];
    downloadCsv(`fixed-asset-tie-out-${asOf}.csv`, headers, dataRows);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="fixedAssets.tie.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }} data-help-section="fixedAssets.tie.filter">
          <Typography variant="h5">Sub-ledger ↔ GL Tie-out</Typography>
          <TextField
            data-help="fixedAssets.tie.asOf"
            label="As of"
            type="date"
            value={asOf}
            onChange={e => setAsOf(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button data-help="fixedAssets.tie.run" variant="contained" onClick={load} disabled={loading}>Run</Button>
        </Box>
        <Button
          data-help="fixedAssets.tie.export"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!report || loading}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : report && (
        <>
          {report.inBalance ? (
            <Alert severity="success" sx={{ mb: 2 }}>Sub-ledger ties to GL.</Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }}>
              Variance detected — {money(report.costVariance)} cost / {money(report.accumulatedVariance)} accumulated.
              Common causes: an asset whose cost account and accumulated-depreciation account are the
              same account; journal entries posted by hand to the asset or depreciation accounts; or
              assets that were added to the register without matching ledger entries.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table data-help-section="fixedAssets.tie.table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="fixedAssets.tie.col.measure">Measure</TableCell>
                  <TableCell data-help="fixedAssets.tie.col.register" align="right">Register (sub-ledger)</TableCell>
                  <TableCell data-help="fixedAssets.tie.col.gl" align="right">General ledger</TableCell>
                  <TableCell data-help="fixedAssets.tie.col.variance" align="right">Variance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Asset cost</TableCell>
                  <TableCell align="right">{money(report.registerCost)}</TableCell>
                  <TableCell align="right">{money(report.glAssetBalance)}</TableCell>
                  <TableCell align="right" sx={{ color: report.costVariance !== 0 ? 'error.main' : 'inherit' }}>
                    {money(report.costVariance)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Accumulated depreciation</TableCell>
                  <TableCell align="right">{money(report.registerAccumulated)}</TableCell>
                  <TableCell align="right">{money(report.glAccumulatedBalance)}</TableCell>
                  <TableCell align="right" sx={{ color: report.accumulatedVariance !== 0 ? 'error.main' : 'inherit' }}>
                    {money(report.accumulatedVariance)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default FixedAssetTieOutReport;
